import { SubscriptionPlan } from '../enums/subscription-plan.enum';
import { SubscriptionFeatures, RateLimitConfig } from '../models/subscription.model';

export interface PlanCatalogEntry {
  plan: SubscriptionPlan;
  price: number;
  label: string;
  isCustomPricing: boolean;
}

export const PLAN_CATALOG: Record<SubscriptionPlan, PlanCatalogEntry> = {
  [SubscriptionPlan.FREE]: {
    plan: SubscriptionPlan.FREE,
    price: 0,
    label: 'Free',
    isCustomPricing: false,
  },
  [SubscriptionPlan.STARTER]: {
    plan: SubscriptionPlan.STARTER,
    price: 29,
    label: 'Starter',
    isCustomPricing: false,
  },
  [SubscriptionPlan.GROWTH]: {
    plan: SubscriptionPlan.GROWTH,
    price: 79,
    label: 'Growth',
    isCustomPricing: false,
  },
  [SubscriptionPlan.PRO]: {
    plan: SubscriptionPlan.PRO,
    price: 149,
    label: 'Pro',
    isCustomPricing: false,
  },
  [SubscriptionPlan.SCALE]: {
    plan: SubscriptionPlan.SCALE,
    price: 0,
    label: 'Scale',
    isCustomPricing: true,
  },
};

export function getPlanPrice(plan: SubscriptionPlan): number {
  return PLAN_CATALOG[plan].price;
}

export interface PlanConfig {
  maxLinks: number;
  maxClicksPerLink: number;
  analyticsRetentionDays: number;
  features: SubscriptionFeatures;
  rateLimit?: RateLimitConfig;
}

const NO_FEATURES: SubscriptionFeatures = {
  deferredDeepLinking: false,
  installTracking: false,
  conversionTracking: false,
  dynamicParameters: false,
  multiChannel: false,
  advancedDashboard: false,
  dataExport: false,
  revenueTracking: false,
  advancedAttribution: false,
  apiAccess: false,
  customIntegrations: false,
  customShortId: false,
};

const STARTER_FEATURES: SubscriptionFeatures = {
  ...NO_FEATURES,
  deferredDeepLinking: true,
  installTracking: true,
  customShortId: true,
};

const GROWTH_FEATURES: SubscriptionFeatures = {
  ...STARTER_FEATURES,
  conversionTracking: true,
  dynamicParameters: true,
  multiChannel: true,
  advancedDashboard: true,
  dataExport: true,
};

const ALL_FEATURES: SubscriptionFeatures = {
  deferredDeepLinking: true,
  installTracking: true,
  conversionTracking: true,
  dynamicParameters: true,
  multiChannel: true,
  advancedDashboard: true,
  dataExport: true,
  revenueTracking: true,
  advancedAttribution: true,
  apiAccess: true,
  customIntegrations: true,
  customShortId: true,
};

export interface FeatureMetadata {
  key: keyof SubscriptionFeatures;
  label: string;
  comingSoon: boolean;
}

export const FEATURE_METADATA: FeatureMetadata[] = [
  { key: 'customShortId', label: 'Custom short IDs', comingSoon: false },
  { key: 'deferredDeepLinking', label: 'Deferred deep linking', comingSoon: true },
  { key: 'installTracking', label: 'Install tracking', comingSoon: false },
  { key: 'conversionTracking', label: 'Conversion tracking', comingSoon: true },
  { key: 'dynamicParameters', label: 'Dynamic parameters', comingSoon: false },
  { key: 'multiChannel', label: 'Multi-channel attribution', comingSoon: false },
  { key: 'advancedDashboard', label: 'Advanced dashboard', comingSoon: false },
  { key: 'dataExport', label: 'Data export', comingSoon: false },
  { key: 'revenueTracking', label: 'Revenue tracking', comingSoon: true },
  { key: 'advancedAttribution', label: 'Advanced attribution', comingSoon: true },
  { key: 'apiAccess', label: 'API access', comingSoon: false },
  { key: 'customIntegrations', label: 'Custom integrations', comingSoon: true },
];

export const PLAN_LIMITS: Record<SubscriptionPlan, PlanConfig> = {
  [SubscriptionPlan.FREE]: {
    maxLinks: 3,
    maxClicksPerLink: 5_000,
    analyticsRetentionDays: 30,
    features: NO_FEATURES,
  },
  [SubscriptionPlan.STARTER]: {
    maxLinks: 20,
    maxClicksPerLink: 50_000,
    analyticsRetentionDays: 90,
    features: STARTER_FEATURES,
  },
  [SubscriptionPlan.GROWTH]: {
    maxLinks: -1,
    maxClicksPerLink: 200_000,
    analyticsRetentionDays: 180,
    features: GROWTH_FEATURES,
  },
  [SubscriptionPlan.PRO]: {
    maxLinks: -1,
    maxClicksPerLink: 500_000,
    analyticsRetentionDays: 365,
    features: ALL_FEATURES,
    rateLimit: { capacity: 100, refillPerSec: 10 }, // 600 req/min sustained
  },
  [SubscriptionPlan.SCALE]: {
    maxLinks: -1,
    maxClicksPerLink: -1,
    analyticsRetentionDays: -1,
    features: ALL_FEATURES,
    rateLimit: { capacity: 500, refillPerSec: 100 }, // 6000 req/min sustained
  },
};
